import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { authorizedAxios, useAuth } from "@moonscape/shared";

type UseUkAuditTrailOptions = {
  taxYear: number;
  enabled?: boolean;
};

export type UkAuditTrailResponse = {
  taxYear: number;
  taxSetting: any;
  auditTrail: any[];
};

export const useUkAuditTrail = ({
  taxYear,
  enabled = true,
}: UseUkAuditTrailOptions): UseQueryResult<UkAuditTrailResponse | null, Error> => {
  const { getToken, user } = useAuth();

  const fetchAuditTrail = async (): Promise<UkAuditTrailResponse | null> => {
    const token = await getToken();
    try {
      const response = await authorizedAxios(token).get(
        "/export/tax-report/uk/audit-trail",
        {
          params: { year: taxYear },
          validateStatus: (status) =>
            (status >= 200 && status < 300) || status === 204,
        }
      );

      if (response.status === 204) {
        return null;
      }

      return response.data;
    } catch (error: any) {
      const status = error?.response?.status;
      const code = error?.response?.data?.error;
      if (status === 400 && code === "unsupported_jurisdiction") {
        return null;
      }
      throw error;
    }
  };

  return useQuery({
    queryKey: ["uk_audit_trail", taxYear],
    queryFn: fetchAuditTrail,
    enabled: enabled && !!user?.email && Number.isFinite(taxYear),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });
};
